import Router from 'Router';
import { useAuth0 } from '@auth0/auth0-react';
import styled from '@emotion/styled';
import { colors, ProximaTheme, Spinner, ToasterProvider } from '@innovationdepartment/proxima-ui';
import ErrorBoundaries from 'components/ErrorBoundaries';
import { UserStoreLoader } from 'components/Loaders';
import NavBar from 'components/Navbar';
import { useSessionStorage } from 'hooks';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import FeatureFlagProvider from 'ui/FeatureFlagProvider';
import SpinnerWrapper from 'ui/SpinnerWrapper';
import GaTag from './GaTag';

const AppWrapper = styled('div')`
  background-color: ${colors.bigBangBlack};
  width: 100vw;
  min-height: 100vh;
  display: flex;

  /* proxima-ui NavBar pins its AppBar to the viewport height, which clips the menu on short viewports. */
  & header.MuiAppBar-root {
    height: auto;
    min-height: 100vh;
  }
`;

const App = () => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionStorage = useSessionStorage();

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      sessionStorage.setItem('redirectTo', location.pathname, 1000 * 60 * 30); // 30 mins
      loginWithRedirect({ authorizationParams: { internal: true } });
      return;
    }

    /* if user is logged out when pasting an url and is prompted to log in, this takes them back to pasted url  */
    const redirectTo = sessionStorage.getItem('redirectTo', true);
    if (redirectTo) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, isLoading]);

  if (isLoading || !isAuthenticated) return <Spinner loading />;

  return (
    <FeatureFlagProvider>
      <ToasterProvider>
        <ErrorBoundaries>
          <GaTag>
            <ProximaTheme>
              <SpinnerWrapper>
                <AppWrapper>
                  <UserStoreLoader>
                    <NavBar />
                    <Router />
                  </UserStoreLoader>
                </AppWrapper>
              </SpinnerWrapper>
            </ProximaTheme>
          </GaTag>
        </ErrorBoundaries>
      </ToasterProvider>
    </FeatureFlagProvider>
  );
};

export default App;
